/**
 * Which fields the executive must be asked for, in which order, and whether a
 * fill is valid.
 *
 * activeFields walks the template exactly the way assemble() does — same
 * conditions, same clause selection, same party variants, same repeated table
 * rows — so the form never asks for a field that will not print, and never
 * omits one that will. A calculated field is never asked for; the fields its
 * formula reads are.
 */
import {
  isTier3, itemsOf, PARTY_SIDES, typeInfo, typesForSide,
  type Block, type ClauseEntry, type ContentBlock, type Expr, type Fill, type FillValue, type PartyFill, type PartySide,
  type PartyType, type Sentence, type Template,
} from '@saledeed/shared';
import { conditionHolds, defaultNameFields, fieldMap, isAutoSelected, nameFieldFor, selectedOptions, type Ctx } from './context';
import type { Issue } from './issues';
import { checkValue, isEmpty, isPresence } from './values';

export interface ActiveFields {
  /** Document-scoped field ids, in order of first appearance. */
  document: string[];
  /** Per side, per item index: per-item field ids in order of appearance. */
  parties: Record<PartySide, string[][]>;
}

class Collector {
  readonly document: string[] = [];
  readonly parties: Record<PartySide, string[][]>;
  private readonly expanding = new Set<string>();
  /** For the form: also list calculated fields (after their inputs), to show their results. */
  constructor(fill: Fill, private readonly withCalculated = false) {
    this.parties = {
      seller: fill.parties.seller.map(() => []),
      buyer: fill.parties.buyer.map(() => []),
      property: itemsOf(fill, 'property').map(() => []),
    };
  }
  /**
   * Names listed outside the blocks ("by A, B and C"): asked in each one's box
   * in the place the box's own wording puts them, so they are added last.
   */
  private readonly later: (() => void)[] = [];
  addLater(ctx: Ctx, field: string, forParty: { side: PartySide; index: number }) {
    this.later.push(() => this.add(ctx, field, forParty));
  }
  flush() {
    for (const run of this.later.splice(0)) run();
    return this;
  }
  add(ctx: Ctx, field: string, forParty?: { side: PartySide; index: number }) {
    const def = ctx.fields.get(field);
    if (!def) return;
    const p = def.scope === 'party' ? forParty ?? (ctx.party ? { side: ctx.party.side, index: ctx.party.index } : null) : null;
    if (def.type === 'calculated') {
      // Ask for what the formula reads, not for the result.
      const key = `${field}@${p ? `${p.side}.${p.index}` : ''}`;
      if (!def.formula || this.expanding.has(key)) return;
      this.expanding.add(key);
      const data = p ? itemsOf(ctx.fill, p.side)[p.index] : undefined;
      addInputs(p && data ? { ...ctx, party: { side: p.side, index: p.index, data } } : ctx, this, def.formula);
      this.expanding.delete(key);
      if (!this.withCalculated) return;
    }
    if (def.scope === 'document') {
      if (!this.document.includes(field)) this.document.push(field);
      return;
    }
    if (!p) return;
    const list = this.parties[p.side][p.index];
    if (list && !list.includes(field)) list.push(field);
  }
}

function addInputs(ctx: Ctx, c: Collector, expr: Expr): void {
  if ('num' in expr) return;
  if ('field' in expr) { c.add(ctx, expr.field); return; }
  if ('sum' in expr) {
    itemsOf(ctx.fill, expr.side).forEach((_, index) => c.add(ctx, expr.sum, { side: expr.side, index }));
    return;
  }
  if ('op' in expr) { addInputs(ctx, c, expr.a); addInputs(ctx, c, expr.b); return; }
  addInputs(ctx, c, expr.if.a);
  addInputs(ctx, c, expr.if.b);
  addInputs(ctx, c, expr.then);
  addInputs(ctx, c, expr.else);
}

function collectSentence(ctx: Ctx, c: Collector, s: Sentence) {
  if (s.when) {
    c.add(ctx, s.when.field); // the deciding field is always asked for
    if (!conditionHolds(ctx, s.when)) return;
  }
  for (const run of s.runs) {
    const runs = run.kind === 'fragment' ? run.runs : [run];
    if (run.kind === 'fragment' && run.when) {
      c.add(ctx, run.when.field);
      if (!conditionHolds(ctx, run.when)) continue;
    }
    for (const r of runs) {
      if (r.kind === 'placeholder') c.add(ctx, r.field);
      else if (r.kind === 'partyRef') {
        itemsOf(ctx.fill, r.side).forEach((p, index) => {
          const fid = nameFieldFor(ctx, r.side, p.type, r.fields);
          if (fid) c.addLater(ctx, fid, { side: r.side, index });
        });
      }
    }
  }
}

function collectContent(ctx: Ctx, c: Collector, b: ContentBlock) {
  switch (b.type) {
    case 'heading': collectSentence(ctx, c, b.sentence); return;
    case 'paragraph': b.sentences.forEach(s => collectSentence(ctx, c, s)); return;
    case 'list': b.items.forEach(it => it.sentences.forEach(s => collectSentence(ctx, c, s))); return;
    case 'table':
      b.rows.forEach((r, ri) => {
        const cellsOf = (rctx: Ctx) => r.cells.forEach(cell => cell.sentences.forEach(s => collectSentence(rctx, c, s)));
        if (r.repeat && !(b.headerRow && ri === 0)) {
          const side = r.repeat;
          itemsOf(ctx.fill, side).forEach((data, index) => cellsOf({ ...ctx, party: { side, index, data } }));
        } else cellsOf(ctx);
      });
      return;
  }
}

function collectBlock(ctx: Ctx, c: Collector, b: Block) {
  switch (b.type) {
    case 'clauseSection':
      for (const entry of b.entries) {
        for (const opt of selectedOptions(entry, ctx.fill)) opt.body.forEach(p => collectContent(ctx, c, p));
      }
      return;
    case 'partyBlock':
      itemsOf(ctx.fill, b.side).forEach((data, index) => {
        const variant = b.variants[data.type];
        if (!variant) return;
        const pctx: Ctx = { ...ctx, party: { side: b.side, index, data } };
        variant.forEach(cb => collectContent(pctx, c, cb));
      });
      return;
    default:
      collectContent(ctx, c, b);
  }
}

/**
 * `withCalculated`: also list calculated fields that print (for the form to
 * show their results). Validation leaves it off: they are never typed.
 */
export function activeFields(template: Template, fill: Fill, opts: { withCalculated?: boolean } = {}): ActiveFields {
  const ctx: Ctx = { fields: fieldMap(template), fill, party: null, names: defaultNameFields(template) };
  const c = new Collector(fill, opts.withCalculated ?? false);
  template.blocks.forEach(b => collectBlock(ctx, c, b));
  c.flush();
  return { document: c.document, parties: c.parties };
}

/* ============================================================ outline */

/**
 * The fill form in the ADMIN's order (owner, 2026-09-30: "the executive should
 * get the details in the sequence the admin defined"). Each section starts at
 * the template's own heading. A side's section (sellers, buyers, properties)
 * sits where its first block is; all its items are filled there in one go.
 * Whole-document fields that only appear inside that side's wording are shown
 * there too, so nothing about the sellers is asked somewhere else.
 */
export type OutlineSection =
  | { kind: 'fields'; title: string | null; fields: string[] }
  | { kind: 'side'; side: PartySide; title: string | null; sharedFields: string[] }
  | { kind: 'clauses'; title: string | null; entries: ClauseEntry[]; fields: string[] };

function headingText(s: Sentence): string {
  return s.runs.map(r => (r.kind === 'text' ? r.text : r.kind === 'fragment' ? r.runs.map(x => (x.kind === 'text' ? x.text : '')).join('') : ''))
    .join('').replace(/\s+/g, ' ').trim();
}

export function formOutline(template: Template, fill: Fill): OutlineSection[] {
  const ctx: Ctx = { fields: fieldMap(template), fill, party: null, names: defaultNameFields(template) };
  const out: OutlineSection[] = [];
  const seen = new Set<string>();
  let title: string | null = null;
  const sideSection = (side: PartySide) => {
    let s = out.find((x): x is Extract<OutlineSection, { kind: 'side' }> => x.kind === 'side' && x.side === side);
    if (!s) { s = { kind: 'side', side, title, sharedFields: [] }; out.push(s); }
    return s;
  };
  const fresh = (b: Block): string[] => {
    const c = new Collector(fill, true);
    collectBlock(ctx, c, b);
    c.flush();
    const ids = c.document.filter(id => !seen.has(id));
    ids.forEach(id => seen.add(id));
    return ids;
  };

  template.blocks.forEach((b, i) => {
    if (b.type === 'heading') {
      // The document's own title is not a section name.
      title = i === 0 && b.level === 1 ? null : headingText(b.sentence) || title;
      const ids = fresh(b);
      if (ids.length) out.push({ kind: 'fields', title, fields: ids });
      return;
    }
    if (b.type === 'partyBlock') {
      sideSection(b.side).sharedFields.push(...fresh(b));
      // The block used up its heading; text after it (with no new heading) is "other details".
      title = null;
      return;
    }
    if (b.type === 'clauseSection') {
      out.push({ kind: 'clauses', title, entries: b.entries, fields: fresh(b) });
      return;
    }
    // A table with rows repeated per item brings that side's section here if it has no block.
    if (b.type === 'table') for (const r of b.rows) if (r.repeat && !template.blocks.some(x => x.type === 'partyBlock' && x.side === r.repeat)) sideSection(r.repeat);
    const ids = fresh(b);
    if (!ids.length) return;
    const last = out[out.length - 1];
    if (last?.kind === 'fields' && last.title === title) last.fields.push(...ids);
    else out.push({ kind: 'fields', title, fields: ids });
  });
  return out;
}

/* =================================================================== */

/**
 * Types an executive may pick on a side: only types that EVERY block of that
 * side has wording for (the same rule as party.typeUnavailable), in the
 * admin's order: built-in first, then his own.
 */
export function partyTypesFor(template: Template, side: PartySide): PartyType[] {
  const blocks = template.blocks.filter((b): b is Extract<Block, { type: 'partyBlock' }> => b.type === 'partyBlock' && b.side === side);
  if (!blocks.length) {
    // A side used only in repeated table rows has no wording to choose between.
    return usesSide(template, side) ? [typesForSide(template, side)[0]!.id] : [];
  }
  return typesForSide(template, side).map(t => t.id).filter(t => blocks.every(b => b.variants[t] !== undefined));
}

function usesSide(template: Template, side: PartySide): boolean {
  return template.blocks.some(b => b.type === 'table' && b.rows.some(r => r.repeat === side));
}

/** Sides the template has a section for (a block, or rows repeated per item). */
export function partySides(template: Template): PartySide[] {
  return PARTY_SIDES.filter(s => template.blocks.some(b => b.type === 'partyBlock' && b.side === s) || usesSide(template, s));
}

/** Whether only one item is allowed on a side (the admin ticked "only one"). */
export function isSingle(template: Template, side: PartySide): boolean {
  return template.blocks.some(b => b.type === 'partyBlock' && b.side === side && b.single === true);
}

/**
 * The copy of a fill that may leave the device (PLAN.md 3.2): every Tier 3
 * value becomes `{ present: true }` when filled and disappears when empty.
 * Calculated fields are never sent (the server works them out). Everything
 * else is copied as it is. The browser sends only this.
 */
export function toServerFill(template: Template, fill: Fill): Fill {
  const fields = fieldMap(template);
  const strip = (values: Record<string, FillValue>): Record<string, FillValue> => {
    const out: Record<string, FillValue> = {};
    for (const [key, value] of Object.entries(values)) {
      const def = fields.get(key);
      if (def?.type === 'calculated') continue;
      if (def && isTier3(def.type)) {
        if (!isEmpty(value)) out[key] = { present: true };
        continue;
      }
      out[key] = value;
    }
    return out;
  };
  const side = (list: PartyFill[]) => list.map(p => ({ type: p.type, values: strip(p.values) }));
  return {
    values: strip(fill.values),
    parties: { seller: side(fill.parties.seller), buyer: side(fill.parties.buyer), property: side(itemsOf(fill, 'property')) },
    clauses: Object.fromEntries(Object.entries(fill.clauses).map(([k, v]) => [k, [...v]])),
  };
}

export interface MissingOnDevice {
  /** Where, in validateFill path style: values.x or parties.seller[0].values.x */
  path: string;
  field: string;
  label: string;
}

/**
 * Tier 3 fields that will print but hold only `{ present: true }`: the value
 * was typed on another device, or this browser's storage was cleared. They
 * must be typed again on this device before the PDF can be made.
 */
export function missingOnDevice(template: Template, fill: Fill): MissingOnDevice[] {
  const fields = fieldMap(template);
  const active = activeFields(template, fill);
  const out: MissingOnDevice[] = [];
  const check = (ids: readonly string[], values: Record<string, FillValue>, path: string) => {
    for (const id of ids) {
      const def = fields.get(id);
      if (def && isTier3(def.type) && isPresence(values[id])) out.push({ path: `${path}.${id}`, field: id, label: def.label });
    }
  };
  check(active.document, fill.values, 'values');
  for (const side of PARTY_SIDES) {
    active.parties[side].forEach((ids, i) => check(ids, itemsOf(fill, side)[i]!.values, `parties.${side}[${i}].values`));
  }
  return out;
}

/* =================================================================== */

export interface FillValidationOptions {
  /**
   * `server`: the Worker. Any Tier 3 value that is a string (rather than
   * `{ present: true }`) is an error — PII must never have been sent.
   * `browser`: full checks including Aadhaar checksum and PAN format.
   */
  context: 'server' | 'browser';
}

const SIDE_WORD: Record<PartySide, string> = { seller: 'seller', buyer: 'buyer', property: 'property' };

export function validateFill(template: Template, fill: Fill, opts: FillValidationOptions): Issue[] {
  const fields = fieldMap(template);
  const issues: Issue[] = [];
  const error = (code: string, path: string, message: string) => issues.push({ severity: 'error', code, path, message });

  const checkKeys = (values: Record<string, FillValue>, scope: 'document' | 'party', path: string) => {
    for (const [key, value] of Object.entries(values)) {
      const def = fields.get(key);
      const vpath = `${path}.${key}`;
      if (!def || def.scope !== scope) {
        error('field.unknown', vpath, `Unknown ${scope === 'party' ? 'seller/buyer/property' : 'document'} field "${key}"`);
        continue;
      }
      if (def.type === 'calculated') {
        error('field.calculated', vpath, `"${def.label}" is worked out by the template and cannot be typed`);
        continue;
      }
      if (isTier3(def.type) && typeof value === 'string' && opts.context === 'server') {
        // Deliberately does not include the value.
        error('tier3.valueOnServer', vpath, `"${def.label}" must never be sent to the server`);
      }
      if (!isTier3(def.type) && isPresence(value)) {
        error('value.presenceNotAllowed', vpath, `"${def.label}" needs an actual value`);
      }
    }
  };

  checkKeys(fill.values, 'document', 'values');

  /* ---- sellers, buyers, properties ---- */
  const sides = new Set(partySides(template));
  for (const side of PARTY_SIDES) {
    const parties = itemsOf(fill, side);
    if (!sides.has(side)) {
      if (parties.length) error('party.unexpected', `parties.${side}`, `This template has no ${SIDE_WORD[side]} section`);
      continue;
    }
    if (!parties.length) error('party.none', `parties.${side}`, `Add at least one ${SIDE_WORD[side]}`);
    if (parties.length > 1 && isSingle(template, side)) error('party.tooMany', `parties.${side}`, `This template allows only one ${SIDE_WORD[side]}`);
    const allowed = new Set(partyTypesFor(template, side));
    parties.forEach((p, i) => {
      const ppath = `parties.${side}[${i}]`;
      if (!allowed.has(p.type)) {
        error('party.typeUnavailable', `${ppath}.type`, `This template has no wording for a ${typeInfo(template, p.type)?.label ?? p.type} ${SIDE_WORD[side]}`);
      }
      checkKeys(p.values, 'party', `${ppath}.values`);
    });
  }

  /* ---- clauses ---- */
  const entries = new Map<string, ClauseEntry>();
  template.blocks.forEach(b => { if (b.type === 'clauseSection') b.entries.forEach(e => entries.set(e.id, e)); });

  for (const [entryId, ids] of Object.entries(fill.clauses)) {
    const entry = entries.get(entryId);
    const cpath = `clauses.${entryId}`;
    if (!entry) { error('clause.unknownEntry', cpath, 'Selection refers to a clause that does not exist'); continue; }
    if (new Set(ids).size !== ids.length) error('clause.duplicate', cpath, `"${entry.label}": the same option is chosen twice`);
    for (const id of ids) {
      if (!entry.options.some(o => o.id === id)) error('clause.unknownOption', cpath, `"${entry.label}": unknown option`);
    }
  }
  for (const entry of entries.values()) {
    if (isAutoSelected(entry)) continue;
    const ids = new Set(fill.clauses[entry.id] ?? []);
    const count = entry.options.filter(o => ids.has(o.id)).length;
    if (count < entry.min || count > entry.max) {
      const rule = entry.min === entry.max ? `exactly ${entry.min}` : `between ${entry.min} and ${entry.max}`;
      error('clause.count', `clauses.${entry.id}`, `"${entry.label}": choose ${rule}`);
    }
  }

  /* ---- required fields and value formats, active fields only ---- */
  const active = activeFields(template, fill);

  const checkActive = (ids: readonly string[], values: Record<string, FillValue>, path: string, holder: 'person' | 'organisation' | null) => {
    for (const id of ids) {
      const def = fields.get(id)!;
      const value = values[id];
      if (isEmpty(value)) {
        if (def.required) error('field.required', `${path}.${id}`, `"${def.label}" is required`);
        continue;
      }
      if (typeof value !== 'string') continue; // presence: checked in the browser
      if (isTier3(def.type) && opts.context === 'server') continue;
      for (const p of checkValue(def, value, holder)) {
        issues.push({ severity: p.severity, code: p.code, path: `${path}.${id}`, message: p.message });
      }
    }
  };

  checkActive(active.document, fill.values, 'values', null);
  for (const side of PARTY_SIDES) {
    active.parties[side].forEach((ids, i) => {
      const p = itemsOf(fill, side)[i]!;
      const kind = typeInfo(template, p.type)?.kind;
      checkActive(ids, p.values, `parties.${side}[${i}].values`, kind === 'person' || kind === 'organisation' ? kind : null);
    });
  }

  return issues;
}
